import React, { useState, useEffect } from 'react';
import { db, auth } from '../firebase';
import { 
  collection, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  doc, 
  query, 
  where, 
  orderBy,
  Timestamp,
  serverTimestamp,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { suggestInterventionGroups as callSuggestInterventionGroups } from '../lib/functions';
import { toast } from 'react-hot-toast';
import { useStudents } from '../lib/useStudents';
import { useSchoolId } from '../lib/auth/schoolContext';
import StaffLeadSelect from './StaffLeadSelect';
import { MAX_GROUP_SIZE, groupSizeError } from '../lib/interventionLimits';

const InterventionGroupManager = () => {
  const [groups, setGroups] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [aiSuggesting, setAiSuggesting] = useState(false);
  const [aiMode, setAiMode] = useState('fill_gaps');
  const [groupSuggestions, setGroupSuggestions] = useState([]);
  const [retireCandidates, setRetireCandidates] = useState([]);
  const [showReviewedSuggestions, setShowReviewedSuggestions] = useState(false);
  const [filters, setFilters] = useState({
    type: 'all',
    status: 'all',
    needs: 'all',
  });

  const { students, loading: studentsLoading } = useStudents();
  const { schoolId } = useSchoolId();

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    interventionType: '',
    targetedNeeds: [],
    schedule: {
      frequency: '',
      duration: '',
      daysOfWeek: [],
      time: '',
    },
    goals: [''],
    studentIds: [],
    staffLead: '',
    status: 'active',
  });

  const [sessionData, setSessionData] = useState({
    date: new Date().toISOString().split('T')[0],
    attendance: [],
    targetScores: [],
    notes: '',
    progress: '',
  });

  useEffect(() => {
    if (schoolId) {
      fetchData();
    }
  }, [schoolId]);

  const fetchData = async () => {
    try {
      setLoading(true);

      // Fetch intervention groups
      const groupsQuery = query(
        collection(db, 'interventionGroups'),
        where('schoolId', '==', schoolId),
        orderBy('createdAt', 'desc')
      );
      const groupsSnap = await getDocs(groupsQuery);
      const groupsData = groupsSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      }));
      setGroups(groupsData);

      // Fetch staff
      const staffQuery = query(
        collection(db, 'staff'),
        where('schoolId', '==', schoolId)
      );
      const staffSnap = await getDocs(staffQuery);
      const staffData = staffSnap.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      })).filter(s => s.active !== false);
      setStaff(staffData);

      const suggestionsQuery = query(
        collection(db, 'interventionSuggestions'),
        where('schoolId', '==', schoolId),
        where('suggestionKind', '==', 'interventionGroup'),
        orderBy('createdAt', 'desc'),
      );
      const suggestionsSnap = await getDocs(suggestionsQuery);
      setGroupSuggestions(suggestionsSnap.docs.map((d) => ({ id: d.id, ...d.data() })));

    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load intervention groups');
    } finally {
      setLoading(false);
    }
  };

  const normalizeGroupName = (name) =>
    String(name || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();

  const buildStudentPayload = () => {
    const inferPhase = (yearGroup) => {
      const yearNum = parseInt(String(yearGroup || '').replace(/[^0-9]/g, ''), 10);
      if (!Number.isNaN(yearNum) && yearNum >= 7) return 'secondary';
      return 'primary';
    };
    return students.map((s) => {
      const secondary = Array.isArray(s.secondaryNeeds) ? s.secondaryNeeds.filter(Boolean) : [];
      const allNeeds = Array.isArray(s.specialNeeds) && s.specialNeeds.length
        ? s.specialNeeds
        : [s.primaryNeed, ...secondary].filter(Boolean);
      return {
        id: s.id,
        firstName: s.firstName || s.name?.split(' ')[0] || 'Unknown',
        lastName: s.lastName || s.name?.split(' ').slice(1).join(' ') || 'Unknown',
        yearGroup: s.yearGroup || 'Year 1',
        phase: s.phase || inferPhase(s.yearGroup),
        specialNeeds: allNeeds,
        secondaryNeeds: secondary,
        primaryNeed: s.primaryNeed || '',
        hasEHCP: !!s.hasEHCP,
        academicLevel: s.academicLevel || 'working at',
        workingAtLevel: s.workingAtLevel || s.academicLevel || '',
        attendance: typeof s.attendance === 'number' ? s.attendance : undefined,
      };
    });
  };

  const handleAiSuggest = async () => {
    if (students.length === 0) {
      toast.error('No students available for AI suggestions');
      return;
    }

    setAiSuggesting(true);
    setRetireCandidates([]);
    try {
      const user = auth.currentUser;
      if (!user) {
        toast.error('Please log in to generate suggestions');
        return;
      }

      const studentData = buildStudentPayload();
      const existingGroups = groups.map((g) => ({
        name: g.name,
        interventionType: g.interventionType,
        studentIds: g.studentIds || [],
        sessions: g.sessions || [],
        status: g.status,
      }));

      const result = await callSuggestInterventionGroups({
        students: studentData,
        mode: aiMode,
        existingGroups,
      });

      const suggested = Array.isArray(result.data?.groups) ? result.data.groups : [];
      if (suggested.length === 0) {
        toast('AI returned no new group suggestions.', { icon: 'ℹ️' });
        return;
      }

      const studentSummaries = students.map((s) => ({
        id: s.id,
        name: `${s.firstName || ''} ${s.lastName || ''}`.trim() || s.name || 'Unknown',
      }));

      await Promise.all(
        suggested.map((g) =>
          addDoc(collection(db, 'interventionSuggestions'), {
            schoolId,
            suggestionKind: 'interventionGroup',
            name: g.name,
            description: g.description || '',
            type: g.interventionType || 'General',
            targetAreas: Array.isArray(g.targetedNeeds) ? g.targetedNeeds : [],
            rationale: Array.isArray(g.goals) ? g.goals.join('; ') : '',
            suggestedSchedule: g.suggestedSchedule || null,
            goals: Array.isArray(g.goals) ? g.goals : [],
            groupSuggestion: g,
            status: 'pending',
            students: Array.isArray(g.studentIds) ? g.studentIds : [],
            studentSummaries,
            createdAt: serverTimestamp(),
            generatedBy: user.uid,
          }),
        ),
      );

      if (Array.isArray(result.data?.retireCandidates) && result.data.retireCandidates.length) {
        setRetireCandidates(result.data.retireCandidates);
      }

      toast.success(`${suggested.length} group suggestion${suggested.length === 1 ? '' : 's'} awaiting review`);
      fetchData();
    } catch (error) {
      console.error('Error getting AI suggestions:', error);
      toast.error('Failed to generate AI suggestions');
    } finally {
      setAiSuggesting(false);
    }
  };

  const acceptGroupSuggestion = async (suggestion) => {
    const payload = suggestion.groupSuggestion || {
      name: suggestion.name,
      description: suggestion.description,
      interventionType: suggestion.type,
      targetedNeeds: suggestion.targetAreas,
      suggestedSchedule: suggestion.suggestedSchedule,
      goals: suggestion.goals,
      studentIds: suggestion.students,
    };
    const sizeErr = groupSizeError((payload.studentIds || []).length);
    if (sizeErr) {
      toast.error(sizeErr);
      return;
    }
    await createGroupFromSuggestion(payload);
    await updateDoc(doc(db, 'interventionSuggestions', suggestion.id), {
      status: 'accepted',
      reviewedAt: Timestamp.now(),
    });
    toast.success(`Group "${payload.name}" created`);
    fetchData();
  };

  const rejectGroupSuggestion = async (suggestionId) => {
    await updateDoc(doc(db, 'interventionSuggestions', suggestionId), {
      status: 'rejected',
      reviewedAt: Timestamp.now(),
    });
    toast.success('Suggestion rejected');
    fetchData();
  };

  const createGroupFromSuggestion = async (suggestion) => {
    const groupData = {
      name: suggestion.name,
      description: suggestion.description,
      interventionType: suggestion.interventionType,
      targetedNeeds: suggestion.targetedNeeds,
      schedule: {
        frequency: suggestion.suggestedSchedule.frequency,
        duration: suggestion.suggestedSchedule.duration,
        daysOfWeek: suggestion.suggestedSchedule.daysOfWeek,
        time: suggestion.suggestedSchedule.time,
      },
      goals: suggestion.goals,
      studentIds: suggestion.studentIds,
      staffLead: null,
      status: 'active',
      sessions: [],
      schoolId: schoolId,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    };

    const docRef = await addDoc(collection(db, 'interventionGroups'), groupData);

    // Update students
    for (const studentId of suggestion.studentIds) {
      const studentRef = doc(db, 'students', studentId);
      await updateDoc(studentRef, {
        interventionGroups: arrayUnion(docRef.id),
        updatedAt: Timestamp.now(),
      });
    }
  };

  const handleCreateGroup = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.interventionType) {
      toast.error('Please fill in required fields');
      return;
    }

    const nameNorm = normalizeGroupName(formData.name);
    const collision = groups.find((g) => normalizeGroupName(g.name) === nameNorm);
    if (collision) {
      toast.error(`A group named "${collision.name}" already exists. Choose a more specific name.`);
      return;
    }

    const sizeErr = groupSizeError((formData.studentIds || []).length);
    if (sizeErr) {
      toast.error(sizeErr);
      return;
    }

    try {
      const groupData = {
        ...formData,
        goals: formData.goals.filter(g => g.trim()),
        sessions: [],
        schoolId: schoolId,
        createdAt: Timestamp.now(),
        updatedAt: Timestamp.now(),
      };

      const docRef = await addDoc(collection(db, 'interventionGroups'), groupData);

      // Update students
      for (const studentId of formData.studentIds) {
        const studentRef = doc(db, 'students', studentId);
        await updateDoc(studentRef, {
          interventionGroups: arrayUnion(docRef.id),
          updatedAt: Timestamp.now(),
        });
      }

      toast.success('Intervention group created successfully');
      setShowCreateModal(false);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error creating group:', error);
      toast.error('Failed to create intervention group');
    }
  };

  const handleUpdateGroup = async (e) => {
    e.preventDefault();

    const sizeErr = groupSizeError((formData.studentIds || []).length);
    if (sizeErr) {
      toast.error(sizeErr);
      return;
    }

    try {
      const groupRef = doc(db, 'interventionGroups', selectedGroup.id);
      
      // Check for added/removed students
      const oldStudentIds = selectedGroup.studentIds || [];
      const newStudentIds = formData.studentIds;
      const addedStudents = newStudentIds.filter(id => !oldStudentIds.includes(id));
      const removedStudents = oldStudentIds.filter(id => !newStudentIds.includes(id));

      await updateDoc(groupRef, {
        ...formData,
        goals: formData.goals.filter(g => g.trim()),
        updatedAt: Timestamp.now(),
      });

      // Update added students
      for (const studentId of addedStudents) {
        const studentRef = doc(db, 'students', studentId);
        await updateDoc(studentRef, {
          interventionGroups: arrayUnion(selectedGroup.id),
          updatedAt: Timestamp.now(),
        });
      }

      // Update removed students
      for (const studentId of removedStudents) {
        const studentRef = doc(db, 'students', studentId);
        await updateDoc(studentRef, {
          interventionGroups: arrayRemove(selectedGroup.id),
          updatedAt: Timestamp.now(),
        });
      }

      toast.success('Intervention group updated successfully');
      setShowEditModal(false);
      setSelectedGroup(null);
      resetForm();
      fetchData();
    } catch (error) {
      console.error('Error updating group:', error);
      toast.error('Failed to update intervention group');
    }
  };

  const handleDeleteGroup = async (groupId) => {
    if (!window.confirm('Are you sure you want to delete this intervention group?')) {
      return;
    }

    try {
      const group = groups.find(g => g.id === groupId);
      
      // Remove from students
      for (const studentId of group.studentIds || []) {
        const studentRef = doc(db, 'students', studentId);
        await updateDoc(studentRef, {
          interventionGroups: arrayRemove(groupId),
          updatedAt: Timestamp.now(),
        });
      }

      await deleteDoc(doc(db, 'interventionGroups', groupId));
      toast.success('Intervention group deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting group:', error);
      toast.error('Failed to delete intervention group');
    }
  };

  const handleLogSession = async (e) => {
    e.preventDefault();

    try {
      const presentIds = new Set(sessionData.attendance || []);
      const presentTargetScores = (sessionData.targetScores || []).filter((ts) =>
        presentIds.has(ts.studentId),
      );

      const groupRef = doc(db, 'interventionGroups', selectedGroup.id);

      // Legacy: keep an inline session record on the group doc so existing
      // analytics that read from `interventionGroups.sessions` still work.
      const session = {
        date: Timestamp.fromDate(new Date(sessionData.date)),
        attendance: sessionData.attendance,
        targetScores: presentTargetScores,
        notes: sessionData.notes,
        progress: sessionData.progress,
      };

      await updateDoc(groupRef, {
        sessions: arrayUnion(session),
        updatedAt: Timestamp.now(),
      });

      // Modern: also write a sessionLogs doc in the canonical shape the
      // InterventionDashboard and progress views consume. The interventionId
      // field points at the group id (interventionGroups doc) so this log is
      // discoverable by the same UI as classic interventions.
      const attendanceList = (selectedGroup.studentIds || []).map((sid) => ({
        studentId: sid,
        name: getStudentName(sid),
        present: presentIds.has(sid),
      }));

      await addDoc(collection(db, 'sessionLogs'), {
        interventionId: selectedGroup.id,
        interventionName: selectedGroup.name || '',
        schoolId,
        date: sessionData.date,
        attendance: attendanceList,
        targetScores: presentTargetScores,
        activities: '',
        progress: sessionData.progress || '',
        challenges: '',
        nextSteps: '',
        notes: sessionData.notes || '',
        status: 'completed',
        createdAt: Timestamp.now(),
      });

      toast.success('Session logged successfully');
      setShowSessionModal(false);
      setSessionData({
        date: new Date().toISOString().split('T')[0],
        attendance: [],
        targetScores: [],
        notes: '',
        progress: '',
      });
      fetchData();
    } catch (error) {
      console.error('Error logging session:', error);
      toast.error('Failed to log session');
    }
  };

  const openEditModal = (group) => {
    setSelectedGroup(group);
    setFormData({
      name: group.name,
      description: group.description,
      interventionType: group.interventionType,
      targetedNeeds: group.targetedNeeds || [],
      schedule: group.schedule || {
        frequency: '',
        duration: '',
        daysOfWeek: [],
        time: '',
      },
      goals: group.goals || [''],
      studentIds: group.studentIds || [],
      staffLead: group.staffLead || '',
      status: group.status || 'active',
    });
    setShowEditModal(true);
  };

  // Normalise targets so this view can consume both modern `targets` (array of
  // {id, text}) and legacy `goals` (array of strings).
  const getGroupTargets = (group) => {
    if (Array.isArray(group?.targets) && group.targets.length > 0) {
      return group.targets
        .slice(0, 3)
        .map((t, i) => ({ id: t.id ?? i + 1, text: t.text ?? String(t) }));
    }
    if (Array.isArray(group?.goals) && group.goals.length > 0) {
      return group.goals
        .filter(Boolean)
        .slice(0, 3)
        .map((g, i) => ({ id: i + 1, text: String(g) }));
    }
    return [];
  };

  const openSessionModal = (group) => {
    setSelectedGroup(group);
    const targets = getGroupTargets(group);
    const ids = group.studentIds || [];
    setSessionData({
      date: new Date().toISOString().split('T')[0],
      attendance: ids,
      targetScores: ids.map((sid) => ({
        studentId: sid,
        name: getStudentName(sid),
        scores: targets.map((t) => ({
          targetId: t.id,
          targetText: t.text,
          status: 'not_met',
        })),
      })),
      notes: '',
      progress: '',
    });
    setShowSessionModal(true);
  };

  const handleTargetScoreChange = (studentId, targetId, status) => {
    setSessionData((prev) => ({
      ...prev,
      targetScores: (prev.targetScores || []).map((ts) =>
        ts.studentId === studentId
          ? {
              ...ts,
              scores: ts.scores.map((s) => (s.targetId === targetId ? { ...s, status } : s)),
            }
          : ts,
      ),
    }));
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      interventionType: '',
      targetedNeeds: [],
      schedule: {
        frequency: '',
        duration: '',
        daysOfWeek: [],
        time: '',
      },
      goals: [''],
      studentIds: [],
      staffLead: '',
      status: 'active',
    });
  };

  const filteredGroups = groups.filter(group => {
    if (filters.type !== 'all' && group.interventionType !== filters.type) return false;
    if (filters.status !== 'all' && group.status !== filters.status) return false;
    if (filters.needs !== 'all' && !group.targetedNeeds?.includes(filters.needs)) return false;
    return true;
  });

  const uniqueTypes = [...new Set(groups.map(g => g.interventionType))];
  const uniqueNeeds = [...new Set(groups.flatMap(g => g.targetedNeeds || []))];

  const pendingGroupSuggestions = groupSuggestions.filter((s) => s.status === 'pending');
  const reviewedGroupSuggestions = groupSuggestions.filter((s) => s.status !== 'pending');

  const getStudentName = (studentId) => {
    const student = students.find(s => s.id === studentId);
    if (!student) return 'Unknown Student';
    return `${student.firstName || ''} ${student.lastName || ''}`.trim();
  };

  const getStaffName = (staffId) => {
    const staffMember = staff.find(s => s.id === staffId);
    return staffMember ? staffMember.name : 'Unassigned';
  };

  if (loading || studentsLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4">
      {/* Header */}
      <div className="mb-8">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">Intervention Groups</h1>
            <p className="text-gray-600">Manage and track student intervention groups</p>
          </div>
          <div className="flex flex-wrap gap-3 items-center">
            <select
              value={aiMode}
              onChange={(e) => setAiMode(e.target.value)}
              disabled={aiSuggesting}
              className="rounded-lg border border-gray-300 px-3 py-2 text-sm"
              title="Fill gaps: new groups only. Term refresh: also flags groups to consider ending."
            >
              <option value="fill_gaps">AI: fill gaps</option>
              <option value="term_refresh">AI: term refresh</option>
            </select>
            <button
              onClick={handleAiSuggest}
              disabled={aiSuggesting}
              className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors flex items-center space-x-2 disabled:opacity-50"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}><path strokeLinecap="round" strokeLinejoin="round" d="M8.25 3v1.5M4.5 8.25H3m18 0h-1.5M4.5 12H3m18 0h-1.5m-15 3.75H3m18 0h-1.5M8.25 19.5V21M12 3v1.5m0 15V21m3.75-18v1.5m0 15V21m-9-1.5h10.5a2.25 2.25 0 002.25-2.25V6.75a2.25 2.25 0 00-2.25-2.25H6.75A2.25 2.25 0 004.5 6.75v10.5a2.25 2.25 0 002.25 2.25zm.75-12h9v9h-9v-9z" /></svg>
              <span>{aiSuggesting ? 'Generating...' : 'AI Suggest Groups'}</span>
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              + Create Group
            </button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-blue-500">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-full">
              <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Groups</p>
              <p className="text-2xl font-semibold text-gray-900">{groups.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-green-500">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-full">
              <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Groups</p>
              <p className="text-2xl font-semibold text-gray-900">
                {groups.filter(g => g.status === 'active').length}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-md border-l-4 border-purple-500">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-full">
              <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
              </svg>
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Students in Groups</p>
              <p className="text-2xl font-semibold text-gray-900">
                {new Set(groups.flatMap(g => g.studentIds || [])).size}
              </p>
            </div>
          </div>
        </div>
      </div>

      {pendingGroupSuggestions.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-amber-900 mb-4">
            Pending AI suggestions ({pendingGroupSuggestions.length})
          </h3>
          <div className="space-y-4">
            {pendingGroupSuggestions.map((suggestion) => (
              <div key={suggestion.id} className="bg-white rounded-lg border border-amber-100 p-4 shadow-sm">
                <div className="flex flex-wrap justify-between gap-3">
                  <div>
                    <span className="inline-block rounded-full bg-yellow-100 text-yellow-800 text-xs font-medium px-2 py-0.5 mb-2">
                      Pending review
                    </span>
                    <h4 className="font-semibold text-gray-900">{suggestion.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{suggestion.description}</p>
                    <p className="text-xs text-gray-500 mt-2">
                      {(suggestion.students || []).length} pupils · {suggestion.type || suggestion.groupSuggestion?.interventionType}
                    </p>
                  </div>
                  <div className="flex gap-2 items-start">
                    <button
                      type="button"
                      onClick={() => acceptGroupSuggestion(suggestion)}
                      className="px-3 py-1.5 bg-green-600 text-white text-sm rounded-md hover:bg-green-700"
                    >
                      Accept
                    </button>
                    <button
                      type="button"
                      onClick={() => rejectGroupSuggestion(suggestion.id)}
                      className="px-3 py-1.5 bg-gray-200 text-gray-800 text-sm rounded-md hover:bg-gray-300"
                    >
                      Reject
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {retireCandidates.length > 0 && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-6 mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-2">Suggested to end (term refresh)</h3>
          <p className="text-sm text-slate-600 mb-3">Review these groups manually. Nothing is archived automatically.</p>
          <ul className="text-sm space-y-1">
            {retireCandidates.map((c, i) => (
              <li key={`${c.name}-${i}`}>
                <span className="font-medium">{c.name}</span> — {c.reason}
              </li>
            ))}
          </ul>
        </div>
      )}

      {reviewedGroupSuggestions.length > 0 && (
        <div className="mb-8">
          <button
            type="button"
            onClick={() => setShowReviewedSuggestions((v) => !v)}
            className="text-sm text-indigo-600 hover:underline"
          >
            {showReviewedSuggestions ? 'Hide' : 'Show'} reviewed AI suggestions ({reviewedGroupSuggestions.length})
          </button>
          {showReviewedSuggestions && (
            <ul className="mt-3 text-sm text-gray-600 space-y-1">
              {reviewedGroupSuggestions.map((s) => (
                <li key={s.id}>
                  {s.name} — <span className="capitalize">{s.status}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white p-6 rounded-lg shadow-md mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Type</label>
            <select
              value={filters.type}
              onChange={(e) => setFilters(prev => ({ ...prev, type: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Types</option>
              {uniqueTypes.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => setFilters(prev => ({ ...prev, status: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="completed">Completed</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Targeted Needs</label>
            <select
              value={filters.needs}
              onChange={(e) => setFilters(prev => ({ ...prev, needs: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All Needs</option>
              {uniqueNeeds.map(need => (
                <option key={need} value={need}>{need}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Groups List */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            Groups ({filteredGroups.length})
          </h3>
        </div>

        {filteredGroups.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <p>No intervention groups found. Create one or use AI suggestions to get started.</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredGroups.map(group => (
              <div key={group.id} className="p-6 hover:bg-gray-50">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <h4 className="text-lg font-semibold text-gray-900">{group.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                    group.status === 'active' ? 'bg-green-100 text-green-800' :
                    group.status === 'inactive' ? 'bg-gray-100 text-gray-800' :
                    'bg-blue-100 text-blue-800'
                  }`}>
                    {group.status}
                  </span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Intervention Type</p>
                    <p className="text-sm text-gray-900">{group.interventionType}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700">Schedule</p>
                    <p className="text-sm text-gray-900">
                      {group.schedule?.frequency} • {group.schedule?.duration} • {group.schedule?.time}
                    </p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700">Staff Lead</p>
                    <p className="text-sm text-gray-900">{getStaffName(group.staffLead)}</p>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700">Sessions Logged</p>
                    <p className="text-sm text-gray-900">{group.sessions?.length || 0}</p>
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Students ({group.studentIds?.length || 0})</p>
                  <div className="flex flex-wrap gap-2">
                    {(group.studentIds || []).map(studentId => (
                      <span key={studentId} className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                        {getStudentName(studentId)}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Targeted Needs</p>
                  <div className="flex flex-wrap gap-2">
                    {(group.targetedNeeds || []).map((need, idx) => (
                      <span key={idx} className="px-2 py-1 bg-purple-100 text-purple-800 rounded text-xs">
                        {need}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="mb-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Goals</p>
                  <ul className="list-disc list-inside text-sm text-gray-900 space-y-1">
                    {(group.goals || []).map((goal, idx) => (
                      <li key={idx}>{goal}</li>
                    ))}
                  </ul>
                </div>

                <div className="flex gap-2">
                  <button
                    onClick={() => openEditModal(group)}
                    className="px-3 py-1 text-sm border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => openSessionModal(group)}
                    className="px-3 py-1 text-sm border border-blue-600 text-blue-600 rounded hover:bg-blue-50"
                  >
                    Log Session
                  </button>
                  <button
                    onClick={() => handleDeleteGroup(group.id)}
                    className="px-3 py-1 text-sm border border-red-600 text-red-600 rounded hover:bg-red-50"
                  >
                    Delete
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || showEditModal) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900">
                {showCreateModal ? 'Create Intervention Group' : 'Edit Intervention Group'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  setShowEditModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            </div>

            <form onSubmit={showCreateModal ? handleCreateGroup : handleUpdateGroup} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Group Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows="3"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Intervention Type *</label>
                <input
                  type="text"
                  value={formData.interventionType}
                  onChange={(e) => setFormData(prev => ({ ...prev, interventionType: e.target.value }))}
                  placeholder="e.g., Reading Support, Math Skills, Social Communication"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Students</label>
                <select
                  multiple
                  value={formData.studentIds}
                  onChange={(e) => {
                    const selected = Array.from(e.target.selectedOptions, option => option.value);
                    if (selected.length > MAX_GROUP_SIZE) {
                      toast.error(groupSizeError(selected.length));
                      return;
                    }
                    setFormData(prev => ({ ...prev, studentIds: selected }));
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  size="6"
                >
                  {students.map(student => (
                    <option key={student.id} value={student.id}>
                      {student.firstName} {student.lastName} - {student.yearGroup}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Hold Ctrl/Cmd to select multiple students (max {MAX_GROUP_SIZE} per group)
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Staff Lead</label>
                <StaffLeadSelect
                  staff={staff}
                  value={formData.staffLead}
                  onChange={(e) => setFormData((prev) => ({ ...prev, staffLead: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Unassigned"
                  emptyLabel="No active staff. Add specialists in Staff Directory"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Frequency</label>
                  <input
                    type="text"
                    value={formData.schedule.frequency}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      schedule: { ...prev.schedule, frequency: e.target.value }
                    }))}
                    placeholder="e.g., 3x per week"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration</label>
                  <input
                    type="text"
                    value={formData.schedule.duration}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      schedule: { ...prev.schedule, duration: e.target.value }
                    }))}
                    placeholder="e.g., 30 minutes"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Time</label>
                <input
                  type="text"
                  value={formData.schedule.time}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    schedule: { ...prev.schedule, time: e.target.value }
                  }))}
                  placeholder="e.g., 10:00 AM"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Goals</label>
                {formData.goals.map((goal, index) => (
                  <div key={index} className="flex gap-2 mb-2">
                    <input
                      type="text"
                      value={goal}
                      onChange={(e) => {
                        const newGoals = [...formData.goals];
                        newGoals[index] = e.target.value;
                        setFormData(prev => ({ ...prev, goals: newGoals }));
                      }}
                      placeholder={`Goal ${index + 1}`}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    {formData.goals.length > 1 && (
                      <button
                        type="button"
                        onClick={() => {
                          const newGoals = formData.goals.filter((_, i) => i !== index);
                          setFormData(prev => ({ ...prev, goals: newGoals }));
                        }}
                        className="px-3 py-2 text-red-600 border border-red-600 rounded hover:bg-red-50"
                      >
                        Remove
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, goals: [...prev.goals, ''] }))}
                  className="text-sm text-blue-600 hover:underline"
                >
                  + Add Goal
                </button>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                  <option value="completed">Completed</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  {showCreateModal ? 'Create Group' : 'Update Group'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Log Session Modal */}
      {showSessionModal && selectedGroup && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] flex flex-col">
            <div className="px-6 py-4 border-b flex items-center justify-between flex-shrink-0">
              <h3 className="text-xl font-semibold text-gray-900">
                Log Session - {selectedGroup.name}
              </h3>
              <button
                onClick={() => {
                  setShowSessionModal(false);
                  setSelectedGroup(null);
                }}
                className="text-gray-500 hover:text-gray-800"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleLogSession} className="flex flex-col flex-1 min-h-0">
              <div className="p-6 space-y-6 overflow-y-auto flex-1">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                  <input
                    type="date"
                    value={sessionData.date}
                    onChange={(e) => setSessionData(prev => ({ ...prev, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Attendance</label>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    {selectedGroup.studentIds?.map(studentId => {
                      const present = sessionData.attendance.includes(studentId);
                      return (
                        <label key={studentId} className="flex items-center space-x-2 p-2 border border-gray-200 rounded-md hover:bg-gray-50">
                          <input
                            type="checkbox"
                            checked={present}
                            onChange={(e) => {
                              const next = e.target.checked
                                ? [...sessionData.attendance, studentId]
                                : sessionData.attendance.filter(id => id !== studentId);
                              setSessionData(prev => ({ ...prev, attendance: next }));
                            }}
                            className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                          />
                          <span className="text-sm text-gray-700">{getStudentName(studentId)}</span>
                          <span className={`ml-auto text-xs px-2 py-0.5 rounded ${present ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                            {present ? 'Present' : 'Absent'}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                </div>

                {/* SMART target scoring — pill buttons per student, per target */}
                {(() => {
                  const groupTargets = getGroupTargets(selectedGroup);
                  if (groupTargets.length === 0) {
                    return (
                      <div className="border border-amber-200 bg-amber-50 rounded-md p-4 text-sm text-amber-900">
                        <p className="font-medium mb-1">No SMART targets set for this group.</p>
                        <p className="text-amber-800">Add SMART targets in the group editor (or use the AI suggest button on a classic intervention) so this session can be scored per student.</p>
                      </div>
                    );
                  }
                  return (
                    <div className="border-2 border-blue-200 rounded-lg p-4 bg-blue-50/30">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-base font-semibold text-gray-800">Target Scoring</h4>
                        <span className="text-xs text-gray-500">Per student, per target</span>
                      </div>
                      <div className="space-y-4">
                        {(sessionData.targetScores || [])
                          .filter(ts => (sessionData.attendance || []).includes(ts.studentId))
                          .map(ts => (
                            <div key={ts.studentId} className="bg-white border border-gray-200 rounded-md shadow-sm">
                              <div className="px-3 py-2 bg-gray-50 font-medium text-gray-800 rounded-t-md">
                                {ts.name}
                              </div>
                              <div className="p-3 space-y-3">
                                {ts.scores.map(score => (
                                  <div key={score.targetId} className="space-y-2">
                                    <p className="text-sm text-gray-800 leading-snug">
                                      <span className="text-gray-500">Target:</span> {score.targetText}
                                    </p>
                                    <div className="flex flex-wrap gap-2" role="radiogroup" aria-label={`Status for ${ts.name}`}>
                                      {[
                                        { value: 'not_met', label: 'Not met', activeClasses: 'bg-red-600 text-white border-red-600', idleClasses: 'bg-white text-red-700 border-red-300 hover:bg-red-50' },
                                        { value: 'partially_met', label: 'Partially met', activeClasses: 'bg-amber-500 text-white border-amber-500', idleClasses: 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50' },
                                        { value: 'achieved', label: 'Achieved', activeClasses: 'bg-green-600 text-white border-green-600', idleClasses: 'bg-white text-green-700 border-green-300 hover:bg-green-50' },
                                      ].map(opt => {
                                        const isActive = score.status === opt.value;
                                        return (
                                          <button
                                            type="button"
                                            key={opt.value}
                                            role="radio"
                                            aria-checked={isActive}
                                            onClick={() => handleTargetScoreChange(ts.studentId, score.targetId, opt.value)}
                                            className={`text-sm px-3 py-1.5 border rounded-full font-medium transition-colors ${isActive ? opt.activeClasses : opt.idleClasses}`}
                                          >
                                            {opt.label}
                                          </button>
                                        );
                                      })}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                      </div>
                    </div>
                  );
                })()}

                <details className="border border-gray-200 rounded-md">
                  <summary className="px-4 py-3 cursor-pointer text-sm font-medium text-gray-700 select-none">
                    Optional context (only fill if something notable happened)
                  </summary>
                  <div className="p-4 space-y-4 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                      <textarea
                        value={sessionData.notes}
                        onChange={(e) => setSessionData(prev => ({ ...prev, notes: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="2"
                        placeholder="Session notes, observations, activities..."
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Progress</label>
                      <textarea
                        value={sessionData.progress}
                        onChange={(e) => setSessionData(prev => ({ ...prev, progress: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        rows="2"
                        placeholder="Student progress, achievements, next steps..."
                      />
                    </div>
                  </div>
                </details>
              </div>

              <div className="flex justify-end gap-3 px-6 py-4 border-t flex-shrink-0 bg-white rounded-b-lg">
                <button
                  type="button"
                  onClick={() => {
                    setShowSessionModal(false);
                    setSelectedGroup(null);
                  }}
                  className="px-4 py-2 border border-gray-300 rounded hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Log Session
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default InterventionGroupManager;

